/**
 * Das Fachmodell des Demonstrationsstands — `DEMO-001`.
 *
 * **Warum die Typen hier stehen und nicht in `packages/domain/**`.** Die
 * Struktur aus `AGENTS.md` sieht `domain/jobs`, `domain/customers` und
 * `domain/intake` vor, und dort gehören sie am Ende auch hin. Nur gehören
 * diese drei Pfade `VIK-002`, `PORTAL-002` und `INBOX-001` — drei Paketen,
 * die noch nicht begonnen sind. Sie heute zu belegen hieße, drei fremde
 * Zuschnitte vorwegzunehmen, bevor der jeweilige Adapter überhaupt weiß, was
 * er liefern kann.
 *
 * Deshalb ein Ort, ein Besitzer, ein Name, der sagt was er ist. Wenn die
 * Adapter kommen, ist das Umhängen eine benannte Aufgabe mit einem
 * Suchbegriff — und nicht eine stille Doppelung, die niemandem auffällt.
 *
 * **Was das Modell nicht ist.** Es ist kein Speicherschema. Die Plattform
 * kopiert nach `07` §7.5 keine fremden Datenbestände; führend bleiben Vikunja,
 * Nextcloud, Paperless und Stalwart. Was hier steht, ist die *Projektion*, die
 * das Portal anzeigt — genau die Felder, die eine Oberfläche braucht, jeweils
 * an einem ResourceRef aufgehängt, der zurück ins Quellsystem zeigt.
 *
 * Die Import-Grenze aus ADR-016 gilt: kein React, kein HTTP, keine Node-API.
 */

import type { ResourceRef, Rolle } from "@hwc/contracts";
import type { EinsatzZustand } from "@hwc/sync";

/**
 * Der Mandant des Demonstrationsstands.
 *
 * `demo` und nicht `mueller` wie in den Beispielen von `04` §4.2, und erst
 * recht nicht der Name des eigenen Betriebs: Der Tenant steht in **jedem**
 * ResourceRef, und `urn:handwerker:demo:vikunja:task:2481` sagt beim Lesen
 * sofort, woran man ist. Ein plausibler Firmenname an dieser Stelle wäre die
 * Sorte Kleinigkeit, die einen synthetischen Bestand später wie einen echten
 * aussehen lässt.
 */
export const DEMO_TENANT = "demo";

/** Kurzform für die Refs dieses Stands — der Tenant ist überall derselbe. */
export function ref(
  provider: ResourceRef["provider"],
  type: string,
  opaqueId: string,
): ResourceRef {
  return { tenant: DEMO_TENANT, provider, type, opaqueId };
}

// ───────────────────────────── Personen ─────────────────────────────

/**
 * Ein Mensch im Kundenstack.
 *
 * `rollen` ist eine Liste, weil `01` §1.6 das so vorsieht und weil der
 * Regelfall im Handwerksbetrieb mit 2–25 Benutzern die Personalunion ist: Der
 * Inhaber ist zugleich Disposition, die Bürokraft macht auch die Buchhaltung.
 * Ein Feld `rolle` würde diesen Betrieb zwingen, sich zu entscheiden.
 */
export interface Person {
  readonly ref: ResourceRef;
  readonly name: string;
  readonly kuerzel: string;
  readonly rollen: readonly Rolle[];
  readonly email: string;
  readonly telefon?: string;
  /** Nur für Monteure: Farbe in Kalender und Tourenliste. */
  readonly farbe?: string;
  /** Steht dieser Monteur heute zur Verfügung? `07` §7.4 fragt danach. */
  readonly verfuegbar?: boolean;
  readonly abwesenheitsgrund?: string;
}

// ───────────────────────────── Kunde ─────────────────────────────

export interface Anschrift {
  readonly strasse: string;
  readonly plz: string;
  readonly ort: string;
}

export interface Kontakt {
  readonly name: string;
  readonly funktion?: string;
  readonly telefon?: string;
  readonly email?: string;
  /**
   * Darf diese Nummer dem Monteur angezeigt werden? `07` §7.9 lädt nur die
   * **freigegebene** Telefonnummer vor — die Durchwahl der Geschäftsführung
   * gehört nicht auf jedes Monteurhandy.
   */
  readonly fuerMonteur: boolean;
}

/**
 * Die Kundenakte. Führendes System ist nach `04` §4.4 das Portal selbst.
 *
 * `externeKundennummer` und `herkunft` sind bewusst von Anfang an dabei, auch
 * wenn sie im Demostand meist leer bleiben: Beim Kundenstamm ist die Richtung
 * zur Branchensoftware noch offen (`CLAUDE.md`), und ein Feld nachzurüsten,
 * wenn schon Datensätze existieren, ist die teurere Reihenfolge.
 */
export interface Kunde {
  readonly ref: ResourceRef;
  readonly nummer: string;
  readonly name: string;
  readonly anschrift: Anschrift;
  readonly kontakte: readonly Kontakt[];
  readonly externeKundennummer?: string;
  readonly herkunft?: "portal" | "branchensoftware";
  readonly notiz?: string;
  readonly seit: string;
}

// ───────────────────────────── Auftrag ─────────────────────────────

/** `04` §4.4 nennt die Folge; `storniert` kommt aus dem Einsatzablauf dazu. */
export const AUFTRAGSSTATUS = [
  "neu",
  "geplant",
  "zugewiesen",
  "in-arbeit",
  "abgeschlossen",
  "geprueft",
  "storniert",
] as const;

export type Auftragsstatus = (typeof AUFTRAGSSTATUS)[number];

/**
 * Der Auftrag — nach `04` §4.4 ein Vikunja-Projekt.
 *
 * Die Nummer ist die, die am Telefon genannt wird. Sie ist **nicht** die
 * Vikunja-Projekt-ID: Fremde numerische IDs erscheinen nach `04` §4.2 nicht
 * in öffentlichen Links, und eine Auftragsnummer landet regelmäßig in einer
 * Mail an den Kunden.
 */
export interface Auftrag {
  readonly ref: ResourceRef;
  readonly nummer: string;
  readonly titel: string;
  readonly kunde: ResourceRef;
  readonly status: Auftragsstatus;
  /** Wer im Büro dafür geradesteht. */
  readonly verantwortlich: ResourceRef;
  readonly angelegt: string;
  readonly beschreibung?: string;
  readonly gewerk?: string;
  /** Anschrift des Einsatzorts, wenn sie von der Kundenanschrift abweicht. */
  readonly einsatzort?: Anschrift;
  /** Gesetzt, wenn dieser Auftrag aus einem Wartungsplan entstand. */
  readonly wartungsplan?: ResourceRef;
}

// ───────────────────────────── Einsatz ─────────────────────────────

export const PRIORITAET = ["niedrig", "normal", "hoch", "notfall"] as const;
export type Prioritaet = (typeof PRIORITAET)[number];

/**
 * Ein Arbeitsschritt — nach ADR-015 eine eigene Vikunja-Unteraufgabe mit
 * eigenem ResourceRef.
 *
 * Der eigene Ref ist der ganze Punkt: `04` §4.12 hält fest, dass zwei
 * Monteure, die ohne Netz verschiedene Schritte abhaken, damit verschiedene
 * Ressourcen ändern — es entsteht kein Konflikt. Mit Ankreuzkästchen im
 * Beschreibungstext hätten sich alle Schritte ein Feld geteilt.
 */
export interface Arbeitsschritt {
  readonly ref: ResourceRef;
  readonly text: string;
  readonly erledigt: boolean;
  /** Muss er erledigt sein, bevor der Einsatz in den Abschluss darf? */
  readonly pflicht: boolean;
  readonly erledigtAm?: string;
  readonly erledigtVon?: ResourceRef;
  /** Begründung, wenn ein Pflichtschritt bewusst offen bleibt (`07` §7.8). */
  readonly ausnahme?: string;
}

/**
 * Der Einsatz — nach `04` §4.4 eine Vikunja-Aufgabe mit Termin, Status und
 * Monteur.
 *
 * **Genau eine Anfahrt je Einsatz.** ADR-045 begründet das über die
 * Abrechenbarkeit: Gäbe es den Rücksprung `uebermittelt → vor-ort`, enthielte
 * ein Einsatz mehrere Vor-Ort-Zeiten mit Lücken, und die Zuordnung von
 * Arbeitszeit zu Anfahrt wäre mehrdeutig. Muss der Monteur nochmal hin, ist
 * das ein Folgeeinsatz — dafür gibt es `folgtAuf`.
 */
export interface Einsatz {
  readonly ref: ResourceRef;
  readonly auftrag: ResourceRef;
  readonly titel: string;
  readonly zustand: EinsatzZustand;
  readonly monteur?: ResourceRef;
  /** Beginn des Zeitfensters, ISO 8601 mit Zone. */
  readonly von: string;
  readonly bis: string;
  readonly prioritaet: Prioritaet;
  readonly schritte: readonly Arbeitsschritt[];
  readonly hinweis?: string;
  /** Folgeeinsatz statt Rücksprung — `07` §7.8. */
  readonly folgtAuf?: ResourceRef;
  /** Warum der Einsatz wartet. Nur bei Zustand `wartet` gesetzt. */
  readonly wartetAuf?: string;
  readonly tatsaechlichAngekommen?: string;
  readonly tatsaechlichAbgeschlossen?: string;
}

// ───────────────────── Auftragszeit und Material ─────────────────────

/**
 * `01` §1.12 trennt scharf: **Auftragszeit** ist die auf einen Einsatz
 * gebuchte Zeit und Grundlage der Abrechnung. **Arbeitszeit** im Sinne der
 * gesetzlichen Aufzeichnungspflicht — Beginn, Pause, Ende eines Arbeitstags —
 * erfasst die Plattform nach ADR-031 ausdrücklich **nicht**.
 *
 * Deshalb heißt der Typ so, wie er heißt. Ein Feld `arbeitszeit` hätte binnen
 * eines Jahres jemanden zu der Annahme verleitet, die Pflicht sei abgedeckt.
 */
export const ZEITART = ["anfahrt", "arbeit", "ruestzeit", "rueckfahrt"] as const;
export type Zeitart = (typeof ZEITART)[number];

export interface Auftragszeit {
  readonly ref: ResourceRef;
  readonly einsatz: ResourceRef;
  readonly monteur: ResourceRef;
  readonly art: Zeitart;
  readonly von: string;
  readonly bis: string;
  /** Geht die Position in die Übergabe an die Branchensoftware? */
  readonly abrechenbar: boolean;
  readonly notiz?: string;
}

export interface Materialposten {
  readonly ref: ResourceRef;
  readonly einsatz: ResourceRef;
  readonly bezeichnung: string;
  readonly menge: number;
  readonly einheit: string;
  readonly artikelnummer?: string;
  /**
   * Die Bestellnummer, unter der dieses Material beim Lieferanten bestellt
   * wurde.
   *
   * **Kein Einstieg in die Bestandsführung** — `02` §2.13 schließt sie aus.
   * Das Feld hat genau einen Zweck: Die Lieferantenrechnung nennt diese
   * Nummer, und sie ist das einzige Merkmal, das sie ohne Zutun einem Auftrag
   * zuordnet. Ohne sie bleibt der häufigste Vorgang im Eingangskorb — die
   * Rechnung eines Großhändlers — genau der, für den kein Vorschlag entsteht.
   *
   * Leer bei allem, was aus dem Lagerbestand des Fahrzeugs kommt.
   */
  readonly bestellnummer?: string;
  readonly abrechenbar: boolean;
  readonly erfasstVon: ResourceRef;
  readonly erfasstAm: string;
}

// ───────────────────────── Eingangsvorgang ─────────────────────────

/** `01` §1.12 nennt die sechs Zustände wörtlich. */
export const EINGANGSSTATUS = [
  "neu",
  "in_pruefung",
  "zugeordnet",
  "zurueckgestellt",
  "abgewiesen",
  "quarantaene",
] as const;

export type Eingangsstatus = (typeof EINGANGSSTATUS)[number];

export const EINGANGSART = ["email", "dokument", "erechnung", "upload"] as const;
export type Eingangsart = (typeof EINGANGSART)[number];

/**
 * Ein automatisch erzeugter Zuordnungsvorschlag.
 *
 * `07` §7.4: „Automatische Vorschläge bleiben bis zur Bestätigung sichtbar als
 * Vorschlag." Deshalb ein eigener Typ neben den bestätigten Feldern und nicht
 * dieselben Felder mit einem Häkchen — ein Vorschlag, der im selben Feld steht
 * wie die Entscheidung, *ist* nach dem ersten Rendern die Entscheidung.
 *
 * Die `begruendung` ist Pflicht. Ein Vorschlag ohne sie ist für die Bürokraft
 * nicht prüfbar, sondern nur glaubwürdig — und das ist der Unterschied
 * zwischen Vorschlag und Automatik.
 */
export interface Zuordnungsvorschlag {
  readonly kunde?: ResourceRef;
  readonly auftrag?: ResourceRef;
  readonly begruendung: string;
  /** 0 bis 1. Wird als Wort angezeigt, nicht als Prozentzahl. */
  readonly sicherheit: number;
}

/**
 * Ein Element des zentralen Eingangskorbs — `07` §7.4.
 *
 * `quelle` zeigt auf die **unveränderte** Ressource im Quellsystem. Der
 * Eingangsvorgang ist nach `01` §1.12 ausdrücklich nicht die E-Mail selbst:
 * Er trägt den Bearbeitungsstatus, die Mail bleibt in Stalwart, wie sie kam.
 */
export interface Eingangsvorgang {
  readonly ref: ResourceRef;
  readonly art: Eingangsart;
  readonly quelle: ResourceRef;
  readonly betreff: string;
  readonly absender: string;
  readonly eingegangen: string;
  readonly status: Eingangsstatus;
  /*
   * **Kein Feld `vorschlag`.** Der Zuordnungsvorschlag wird gerechnet
   * (`berechneVorschlag`) und nicht mitgeführt: Ein zweiter Ort, an dem er
   * stehen kann, ist ein zweiter Ort, an dem er falsch stehen kann — und der
   * mitgeführte gewinnt beim Anzeigen immer, weil er zuerst da ist.
   *
   * Zwei feste Vorschläge standen hier einmal. Einer traf zufällig dasselbe
   * Ziel wie die Regel, der andere begründete sich mit einer Regel, die es
   * nicht gab. Beim Durchklicken sah beides gleich gut aus.
   */
  /** Gesetzt, sobald jemand bestätigt hat. */
  readonly zugeordneterAuftrag?: ResourceRef;
  readonly bearbeiter?: ResourceRef;
  readonly anhaenge: readonly ResourceRef[];
  /** Was bei der Verarbeitung schiefging — `07` §7.4 verlangt die Anzeige. */
  readonly fehler?: string;
  readonly vorschau?: string;
}

// ───────────────────────────── E-Rechnung ─────────────────────────────

export const RECHNUNGSPROFIL = [
  "xrechnung-ubl",
  "xrechnung-cii",
  "zugferd-en16931",
  "zugferd-basic",
  "unbekannt",
] as const;

export type Rechnungsprofil = (typeof RECHNUNGSPROFIL)[number];

/**
 * Die Felder, die die Bürokraft nach `07` §7.16 sieht.
 *
 * `validierung` kennt drei Ausgänge und nicht zwei: Eine formal ungültige
 * Rechnung wird „nicht still verworfen oder als gültig markiert". Ein
 * boolesches `gueltig` hätte den mittleren Fall — gültig, aber mit
 * Anmerkungen — auf eine der beiden Seiten gezwungen.
 */
export interface ERechnung {
  readonly ref: ResourceRef;
  readonly eingangsvorgang: ResourceRef;
  readonly steller: string;
  readonly rechnungsnummer: string;
  readonly datum: string;
  /** In Cent, damit nichts gerundet wird, was jemand später abtippt. */
  readonly betragCent: number;
  readonly waehrung: string;
  readonly profil: Rechnungsprofil;
  readonly validierung: "gueltig" | "mit-anmerkungen" | "ungueltig";
  readonly befunde: readonly string[];
  /** Das unveränderte Original in Paperless. */
  readonly original: ResourceRef;
  /** Hash des Originals — `07` §7.16 verlangt, dass beides zusammen findbar bleibt. */
  readonly originalHash: string;
}

// ───────────────────────── Ablage und Wissen ─────────────────────────

/**
 * `07` §7.12 unterscheidet **fachlich**, nicht technisch: Arbeitsdatei,
 * archiviertes Dokument, Kommunikation, Wissen. Die technische Herkunft ist
 * ein sekundärer Hinweis — deshalb steht `art` vor dem Provider im Ref.
 */
export const ABLAGEART = ["arbeitsdatei", "dokument", "wissen"] as const;
export type Ablageart = (typeof ABLAGEART)[number];

export interface Ablageobjekt {
  readonly ref: ResourceRef;
  readonly art: Ablageart;
  readonly name: string;
  readonly mimetyp: string;
  readonly groesseBytes: number;
  readonly geaendert: string;
  readonly geaendertVon?: string;
  /** Ordnerpfad im Quellsystem, nur als Hinweis. */
  readonly pfad?: string;
  /**
   * Ist der Inhalt in der Monteur-App vorgeladen? `07` §7.9 verlangt die
   * Kennzeichnung, weil der Monteur im Funkloch sonst erst beim Antippen
   * merkt, dass die Anleitung fehlt.
   */
  readonly offline?: "vorgeladen" | "auf-wunsch" | "nur-online";
  /** Bei Wissensseiten: der Volltext, damit die Suche etwas zu tun hat. */
  readonly inhalt?: string;
  readonly schlagworte?: readonly string[];
  /**
   * Frühere Fassungen — `NC-003`, `07` §7.12.
   *
   * Nextcloud führt sie; das Portal zeigt sie an der Datei, weil dort die
   * Frage entsteht („gestern war das noch anders"). Absteigend, neueste
   * zuerst: Wer zurückholt, meint fast immer die vorletzte.
   */
  readonly versionen?: readonly Dateiversion[];
  /** Gesetzt, wenn die Datei im Papierkorb liegt. */
  readonly geloescht?: string;
  readonly geloeschtVon?: string;
}

/**
 * Eine frühere Fassung einer Datei.
 *
 * Kein eigener ResourceRef: Eine Version ist keine Ressource, sondern ein
 * Zustand einer. `04` §4.3 führt `source_version` als optionales Feld an der
 * Kante — dort wird sie referenziert, nicht als eigenes Objekt.
 */
export interface Dateiversion {
  readonly kennung: string;
  readonly zeitpunkt: string;
  readonly groesseBytes: number;
  readonly geaendertVon?: string;
  /** Was sich geändert hat, soweit das Quellsystem es weiß. */
  readonly notiz?: string;
}

// ───────────────────────── Kommunikation ─────────────────────────

export interface Nachricht {
  readonly ref: ResourceRef;
  readonly von: string;
  readonly an: readonly string[];
  readonly betreff: string;
  readonly gesendet: string;
  readonly gelesen: boolean;
  readonly ausschnitt: string;
  readonly anhaenge: readonly ResourceRef[];
  readonly richtung: "eingehend" | "ausgehend";
}

export interface Chatbeitrag {
  readonly ref: ResourceRef;
  readonly raum: ResourceRef;
  readonly autor: ResourceRef;
  readonly gesendet: string;
  readonly text: string;
  /** Bild oder Sprachnachricht — Anrufe gibt es nach ADR-018 nicht. */
  readonly anhang?: ResourceRef;
}

export interface Chatraum {
  readonly ref: ResourceRef;
  readonly auftrag: ResourceRef;
  readonly name: string;
  readonly teilnehmer: readonly ResourceRef[];
}

export interface Termin {
  readonly ref: ResourceRef;
  readonly titel: string;
  readonly von: string;
  readonly bis: string;
  readonly ort?: string;
  readonly teilnehmer: readonly ResourceRef[];
  readonly auftrag?: ResourceRef;
  readonly ganztaegig?: boolean;
}

// ───────────────────────── Servicebericht ─────────────────────────

/** Die fünf Zustände aus `07` §7.16, wörtlich. */
export const BERICHTSZUSTAND = [
  "entwurf",
  "zur_pruefung",
  "freigegeben",
  "veroeffentlicht",
  "ersetzt",
] as const;

export type Berichtszustand = (typeof BERICHTSZUSTAND)[number];

/**
 * Der Servicebericht — Portal-Entwurf, freigegebene PDF-Fassung in Nextcloud.
 *
 * **Kundensicht und interne Notizen sind getrennte Felder.** `07` §7.16 nennt
 * den Grund: damit interne Chats oder nicht freigegebene Medien nicht
 * versehentlich im PDF erscheinen. Ein gemeinsames Feld mit einem Schalter
 * „intern" wäre dieselbe Zusage mit einer Stelle, an der man sie vergessen
 * kann.
 *
 * Die Freigabe bezieht sich immer auf eine **konkrete Version** — deshalb
 * trägt der Bericht eine Versionsnummer und nicht nur einen Zustand.
 */
export interface Servicebericht {
  readonly ref: ResourceRef;
  readonly einsatz: ResourceRef;
  readonly auftrag: ResourceRef;
  readonly version: number;
  readonly zustand: Berichtszustand;
  readonly erstellt: string;
  readonly erstelltVon: ResourceRef;
  readonly taetigkeit: string;
  readonly interneNotiz?: string;
  readonly zeiten: readonly ResourceRef[];
  readonly material: readonly ResourceRef[];
  readonly bilder: readonly ResourceRef[];
  readonly unterschrift?: { readonly name: string; readonly am: string };
  readonly freigegebenVon?: ResourceRef;
  readonly freigegebenAm?: string;
  /** Die veröffentlichte PDF-Fassung in Nextcloud. */
  readonly pdf?: ResourceRef;
  /** Auf welche Vorversion dieser Bericht folgt — Beziehung `supersedes`. */
  readonly ersetzt?: ResourceRef;
}

// ───────────────────────── Wartungsplan ─────────────────────────

/** `07` §7.16 nennt die Felder; `MAINT-001` baut die Mechanik. */
export interface Wartungsplan {
  readonly ref: ResourceRef;
  readonly kunde: ResourceRef;
  readonly titel: string;
  readonly intervallMonate: number;
  readonly bezugsdatum: string;
  readonly vorlaufTage: number;
  readonly zustaendig: ResourceRef;
  readonly naechsteFaelligkeit: string;
  readonly status: "aktiv" | "pausiert" | "beendet";
  readonly letzterEinsatz?: ResourceRef;
}

// ───────────────────────── MagicLink ─────────────────────────

/**
 * Ein befristeter Zugang für Externe ohne Konto — `04` §4.9, sieben Tage.
 *
 * Nicht zu verwechseln mit der Rolle `external-partner`: Der Steuerberater
 * bekommt ein eigenes Konto mit unbefristetem, aber widerrufbarem Leserecht.
 * Beides über MagicLinks abzubilden würde nach `07` §7.2 die Sieben-Tage-Regel
 * aushöhlen.
 */
export interface MagicLink {
  readonly ref: ResourceRef;
  readonly ziel: ResourceRef;
  readonly empfaenger: string;
  readonly erstellt: string;
  readonly laeuftAb: string;
  readonly widerrufen: boolean;
  readonly zugriffe: number;
  readonly zweck: string;
}

// ───────────────────────── Link-Graph ─────────────────────────

/** Die Beziehungstypen aus `04` §4.3, vollständig. */
export const BEZIEHUNG = [
  "belongs_to_job",
  "belongs_to_customer",
  "attachment_of",
  "mentioned_in",
  "published_from",
  "generated_from",
  "received_via",
  "report_for",
  "scheduled_from",
  "supersedes",
  "related_to",
] as const;

export type Beziehung = (typeof BEZIEHUNG)[number];

/**
 * Eine Kante des Link-Graphen — `04` §4.3.
 *
 * `deletedAt` statt harter Löschung: Der Graph trägt Tombstones, damit ein
 * gelöschtes Ziel referenzierbar bleibt und beim Öffnen eine ehrliche Absage
 * liefert statt eines leeren Bildschirms.
 */
export interface Kante {
  readonly source: ResourceRef;
  readonly relation: Beziehung;
  readonly target: ResourceRef;
  readonly createdBy: ResourceRef;
  readonly createdAt: string;
  readonly deletedAt?: string;
}

// ───────────────────────── Auditspur ─────────────────────────

/**
 * `05` §5.15 stellt jeden Statuswechsel unter Auditpflicht, und ADR-045
 * verlangt bei stellvertretendem Schalten **beide** Namen: den Schaltenden und
 * den Zugewiesenen. Deshalb zwei Felder — mit einem wäre die Urheberschaft
 * genau in dem Fall verloren, für den die Regel geschrieben wurde.
 */
export interface Auditeintrag {
  readonly ref: ResourceRef;
  readonly zeitpunkt: string;
  readonly objekt: ResourceRef;
  readonly handlung: string;
  readonly akteur: ResourceRef;
  /** Gesetzt, wenn das Büro stellvertretend für den Monteur geschaltet hat. */
  readonly imAuftragVon?: ResourceRef;
  readonly von?: string;
  readonly nach?: string;
}

// ───────────────────────── Gesamtbestand ─────────────────────────

/**
 * Alles zusammen. Ein Objekt, damit eine Oberfläche eine Quelle hat und nicht
 * ein Dutzend Importe, die einzeln veralten können.
 */
export interface Bestand {
  readonly tenant: string;
  readonly betrieb: {
    readonly name: string;
    readonly anschrift: Anschrift;
    readonly telefon: string;
    readonly email: string;
  };
  readonly personen: readonly Person[];
  readonly kunden: readonly Kunde[];
  readonly auftraege: readonly Auftrag[];
  readonly einsaetze: readonly Einsatz[];
  readonly zeiten: readonly Auftragszeit[];
  readonly material: readonly Materialposten[];
  readonly eingang: readonly Eingangsvorgang[];
  readonly rechnungen: readonly ERechnung[];
  readonly ablage: readonly Ablageobjekt[];
  readonly nachrichten: readonly Nachricht[];
  readonly chatraeume: readonly Chatraum[];
  readonly chatbeitraege: readonly Chatbeitrag[];
  readonly termine: readonly Termin[];
  readonly berichte: readonly Servicebericht[];
  readonly wartungsplaene: readonly Wartungsplan[];
  readonly magiclinks: readonly MagicLink[];
  readonly kanten: readonly Kante[];
  readonly audit: readonly Auditeintrag[];
}
